import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NikLogo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "register") await register(name, email, password);
      else {
        const { data } = await api.post("/auth/forgot-password", { email });
        setResetLink(data.reset_link || "");
        toast.success(data.message || "Reset link created");
        setBusy(false);
        return;
      }
      toast.success("Welcome to KalamPrime");
      navigate("/");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      <img
        src="https://images.unsplash.com/photo-1727018663219-b0bd25a359e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="" className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#0B0E14]/85 backdrop-blur-sm" />

      <div className="relative w-full max-w-md bg-[#121824]/90 border border-white/10 rounded-2xl p-8 fade-up">
        <div className="flex justify-center mb-6"><NikLogo size={40} /></div>
        <h1 className="font-display text-2xl font-bold text-center text-white">
          {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}
        </h1>
        <p className="text-center text-slate-400 text-sm mt-1">
          {mode === "forgot" ? "Enter your email to get a reset link." : "Press Play. Enter Your World."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          {mode === "register" && (
            <input
              data-testid="auth-name-input"
              value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name"
              className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[var(--nik-blue)] transition-colors"
            />
          )}
          <input
            data-testid="auth-email-input" type="email"
            value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
            className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[var(--nik-blue)] transition-colors"
          />
          {mode !== "forgot" && (
            <div className="relative">
              <input
                data-testid="auth-password-input" type={showPassword ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password"
                className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 pr-12 text-white outline-none focus:border-[var(--nik-blue)] transition-colors"
              />
              <button
                type="button"
                data-testid="auth-show-password-btn"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-slate-400 hover:text-white text-xs font-medium"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                <span className="hidden sm:inline">{showPassword ? "Hide" : "Show"}</span>
              </button>
            </div>
          )}
          {mode === "login" && (
            <div className="text-right">
              <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
                className="text-xs text-[var(--nik-glow)] font-medium hover:underline">
                Forgot password?
              </button>
            </div>
          )}
          <button
            data-testid="auth-submit-btn" type="submit" disabled={busy}
            className="w-full bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 nik-glow-btn disabled:opacity-60"
          >
            {busy && <Loader2 size={18} className="animate-spin" />}
            {mode === "login" ? "Sign In" : mode === "register" ? "Sign Up" : "Send Reset Link"}
          </button>
        </form>

        {mode === "forgot" && resetLink && (
          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-3 text-sm">
            <p className="text-slate-300">Email delivery is not configured, so use your reset link directly:</p>
            <a data-testid="reset-password-link" href={resetLink} className="text-[var(--nik-glow)] font-medium break-all hover:underline">
              {resetLink}
            </a>
          </div>
        )}

        {mode !== "forgot" && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-slate-500">OR</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <button
              data-testid="google-login-btn" onClick={googleLogin}
              className="w-full bg-white text-black font-semibold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
          </>
        )}

        <div className="text-center text-sm text-slate-400 mt-5">
          {mode === "forgot" ? (
            <button data-testid="auth-toggle-btn" onClick={() => { setMode("login"); setResetLink(""); }} className="text-[var(--nik-glow)] font-semibold hover:underline">
              Back to Sign In
            </button>
          ) : (
            <>
              {mode === "login" ? "New to KalamPrime? " : "Already have an account? "}
              <button
                data-testid="auth-toggle-btn"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-[var(--nik-glow)] font-semibold hover:underline"
              >
                {mode === "login" ? "Sign up now" : "Sign in"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
